export const LEGAL_NOTICE_REMINDER_STORAGE_KEY = "phoebe:thread-legal-notice:v1";
export const LEGAL_NOTICE_REMINDER_INTERVAL_MS = 72 * 60 * 60 * 1000;

export type LegalNoticeReminderState = {
  intervalMs?: number;
  threadStartedAt: number | null;
  nextShowAt: number | null;
  removedAt: number | null;
};

export const EMPTY_LEGAL_NOTICE_REMINDER_STATE: LegalNoticeReminderState = {
  intervalMs: LEGAL_NOTICE_REMINDER_INTERVAL_MS,
  threadStartedAt: null,
  nextShowAt: null,
  removedAt: null,
};

export function readLegalNoticeReminderState(storage: Storage | undefined = getBrowserStorage()): LegalNoticeReminderState {
  if (!storage) return EMPTY_LEGAL_NOTICE_REMINDER_STATE;

  try {
    const rawValue = storage.getItem(LEGAL_NOTICE_REMINDER_STORAGE_KEY);
    if (!rawValue) return EMPTY_LEGAL_NOTICE_REMINDER_STATE;
    return normalizeLegalNoticeReminderState(JSON.parse(rawValue));
  } catch {
    return EMPTY_LEGAL_NOTICE_REMINDER_STATE;
  }
}

export function writeLegalNoticeReminderState(
  state: LegalNoticeReminderState,
  storage: Storage | undefined = getBrowserStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(LEGAL_NOTICE_REMINDER_STORAGE_KEY, JSON.stringify(normalizeLegalNoticeReminderState(state)));
  } catch {
    // Embedded host pages and privacy modes can block storage. The reminder is best-effort.
  }
}

export function ensureLegalNoticeThreadStarted(
  state: LegalNoticeReminderState,
  now: number = Date.now(),
  intervalMs: number = state.intervalMs ?? LEGAL_NOTICE_REMINDER_INTERVAL_MS,
): LegalNoticeReminderState {
  const validatedIntervalMs = validateLegalNoticeReminderInterval(intervalMs);
  if (
    state.threadStartedAt !== null &&
    state.nextShowAt !== null &&
    (state.intervalMs ?? LEGAL_NOTICE_REMINDER_INTERVAL_MS) === validatedIntervalMs
  ) return state;
  return {
    ...state,
    intervalMs: validatedIntervalMs,
    threadStartedAt: now,
    nextShowAt: now + validatedIntervalMs,
    removedAt: null,
  };
}

export function removeLegalNoticeReminder(
  state: LegalNoticeReminderState,
  now: number = Date.now(),
): LegalNoticeReminderState {
  const intervalMs = normalizeLegalNoticeReminderInterval(state.intervalMs);
  return {
    ...state,
    intervalMs,
    removedAt: now,
    nextShowAt: now + intervalMs,
  };
}

export function shouldShowLegalNoticeReminder(state: LegalNoticeReminderState, now: number = Date.now()): boolean {
  return state.threadStartedAt !== null && state.nextShowAt !== null && now >= state.nextShowAt;
}

function normalizeLegalNoticeReminderState(value: unknown): LegalNoticeReminderState {
  if (!value || typeof value !== "object") return EMPTY_LEGAL_NOTICE_REMINDER_STATE;
  const record = value as Record<string, unknown>;
  return {
    intervalMs: normalizeLegalNoticeReminderInterval(record.intervalMs),
    threadStartedAt: normalizeTimestamp(record.threadStartedAt),
    nextShowAt: normalizeTimestamp(record.nextShowAt),
    removedAt: normalizeTimestamp(record.removedAt),
  };
}

function normalizeTimestamp(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

function normalizeLegalNoticeReminderInterval(value: unknown): number {
  return isValidLegalNoticeReminderInterval(value) ? value : LEGAL_NOTICE_REMINDER_INTERVAL_MS;
}

function validateLegalNoticeReminderInterval(value: unknown): number {
  if (isValidLegalNoticeReminderInterval(value)) return value;
  throw new RangeError("Legal notice reminder interval must be a positive finite number");
}

function isValidLegalNoticeReminderInterval(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 && value <= 2_147_483_647;
}

function getBrowserStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}
